import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Editor } from '@tinymce/tinymce-react';
import { ArrowLeftIcon, PaperClipIcon, TrashIcon, ArrowUpTrayIcon } from '@heroicons/react/24/outline';

const MAX_ATTACHMENT_SIZE = 10 * 1024 * 1024;
const MAX_ATTACHMENTS = 5;
const MAX_TOTAL_ATTACHMENT_SIZE = 20 * 1024 * 1024;
const ALLOWED_EXTENSIONS = ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx', '.jpg', '.jpeg', '.png', '.gif', '.webp'];
const ALL_ORGANISMES = ['SAR', 'AMAIR', 'ADAR'];

const formatFileSize = (bytes) => {
  if (bytes == null) return '';
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
};

export default function AdminCommunicationForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    titre: '',
    contenu: '',
    estSanitaire: false,
    copieOrganismes: [], // ['SAR', 'AMAIR', 'ADAR']
    criteresDestinataires: [], // [{organisme, annee, statut}]
    groupesDestinataires: [], // [{organisme, groupe: 'conseil'|'bureau'}]
    destinataires: 'mon_groupement', // Garder pour compatibilité
    dateProgrammee: ''
  });
  const [destinataireStats, setDestinataireStats] = useState([]);
  const [groupeStats, setGroupeStats] = useState([]);
  const [loadingStats, setLoadingStats] = useState(false);
  const [previewDestinataires, setPreviewDestinataires] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [scheduleDate, setScheduleDate] = useState('');
  const [communicationStatus, setCommunicationStatus] = useState(null);
  const [piecesJointes, setPiecesJointes] = useState([]);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [destinataireTab, setDestinataireTab] = useState('SAR');
  const fileInputRef = useRef(null);

  // Fusionne les stats API avec les critères déjà enregistrés (ex. brouillon créé depuis un événement)
  // pour afficher les cases cochées même si count = 0 dans les stats
  const destinataireStatsMerged = useMemo(() => {
    const keyOf = (s) => `${s.organisme}|${Number(s.annee)}|${s.statut}`;
    const map = new Map();
    destinataireStats.forEach((s) => {
      map.set(keyOf(s), { ...s, annee: Number(s.annee) });
    });
    (formData.criteresDestinataires || []).forEach((c) => {
      const k = keyOf(c);
      if (!map.has(k)) {
        map.set(k, {
          organisme: c.organisme,
          annee: Number(c.annee),
          statut: c.statut,
          count: 0,
        });
      }
    });
    return Array.from(map.values());
  }, [destinataireStats, formData.criteresDestinataires]);

  useEffect(() => {
    if (!user || !['admin', 'super_admin'].includes(user.role)) {
      navigate('/');
      return;
    }
    if (id) {
      fetchCommunication();
    }
    fetchDestinataireStats();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, user, navigate]);

  const fetchDestinataireStats = async () => {
    try {
      setLoadingStats(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/communications/destinataires-stats`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = response.data;
      if (Array.isArray(data)) {
        setDestinataireStats(data);
        setGroupeStats([]);
      } else {
        setDestinataireStats(data.adhesions || []);
        setGroupeStats(data.groupes || []);
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des stats:', error);
    } finally {
      setLoadingStats(false);
    }
  };

  const fetchPreviewDestinataires = async () => {
    try {
      setLoadingPreview(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/communications/preview-destinataires`,
        {
          estSanitaire: formData.estSanitaire,
          criteresDestinataires: formData.criteresDestinataires,
          groupesDestinataires: formData.groupesDestinataires
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPreviewDestinataires(response.data);
    } catch (error) {
      console.error('Erreur lors de la prévisualisation:', error);
      setPreviewDestinataires(null);
    } finally {
      setLoadingPreview(false);
    }
  };

  // Prévisualiser les destinataires quand les critères changent
  useEffect(() => {
    if (formData.estSanitaire || formData.criteresDestinataires.length > 0 || formData.groupesDestinataires.length > 0) {
      fetchPreviewDestinataires();
    } else {
      setPreviewDestinataires(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.estSanitaire, formData.criteresDestinataires, formData.groupesDestinataires]);

  const fetchCommunication = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/communications/${id}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      const comm = response.data;
      setCommunicationStatus(comm.statut);
      setFormData({
        titre: comm.titre,
        contenu: comm.contenu,
        estSanitaire: comm.estSanitaire,
        copieOrganismes: comm.copieOrganismes || [],
        criteresDestinataires: comm.criteresDestinataires || [],
        groupesDestinataires: comm.groupesDestinataires || [],
        destinataires: comm.destinataires,
        dateProgrammee: comm.dateProgrammee ? new Date(comm.dateProgrammee).toISOString().slice(0, 16) : ''
      });
      setPiecesJointes(comm.piecesJointes || []);
      const firstSelectedOrg = ALL_ORGANISMES.find((org) =>
        (comm.criteresDestinataires || []).some((c) => c.organisme === org) ||
        (comm.groupesDestinataires || []).some((g) => g.organisme === org)
      );
      if (firstSelectedOrg) setDestinataireTab(firstSelectedOrg);
    } catch (error) {
      console.error('Erreur lors de la récupération:', error);
      alert('Erreur lors de la récupération de la communication');
      navigate('/admin/communications');
    }
  };

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`
  });

  const uploadFileToCommunication = async (communicationId, file) => {
    const form = new FormData();
    form.append('pieceJointe', file);
    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/communications/${communicationId}/pieces-jointes`,
      form,
      {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        }
      }
    );
    return response.data.pieceJointe;
  };

  const uploadPendingFiles = async (communicationId) => {
    if (pendingFiles.length === 0) return;
    setUploadingFiles(true);
    try {
      const uploaded = [];
      for (const file of pendingFiles) {
        const piece = await uploadFileToCommunication(communicationId, file);
        uploaded.push(piece);
      }
      setPiecesJointes((prev) => [...prev, ...uploaded]);
      setPendingFiles([]);
    } finally {
      setUploadingFiles(false);
    }
  };

  const handleFilesSelected = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (files.length === 0) return;

    const currentCount = piecesJointes.length + pendingFiles.length;
    const currentSize =
      piecesJointes.reduce((sum, pj) => sum + (pj.taille || 0), 0) +
      pendingFiles.reduce((sum, f) => sum + f.size, 0);

    const accepted = [];
    for (const file of files) {
      const ext = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();
      if (!ALLOWED_EXTENSIONS.includes(ext)) {
        alert(`Type de fichier non autorisé : ${file.name}\nFormats acceptés : PDF, Word, Excel, PowerPoint, images.`);
        continue;
      }
      if (file.size > MAX_ATTACHMENT_SIZE) {
        alert(`Le fichier « ${file.name} » dépasse la taille maximale de 10 Mo.`);
        continue;
      }
      if (currentCount + accepted.length >= MAX_ATTACHMENTS) {
        alert(`Maximum ${MAX_ATTACHMENTS} pièces jointes par communication.`);
        break;
      }
      const nextSize = currentSize + accepted.reduce((s, f) => s + f.size, 0) + file.size;
      if (nextSize > MAX_TOTAL_ATTACHMENT_SIZE) {
        alert('La taille totale des pièces jointes ne doit pas dépasser 20 Mo.');
        break;
      }
      accepted.push(file);
    }

    if (accepted.length === 0) return;

    if (id) {
      setUploadingFiles(true);
      try {
        const uploaded = [];
        for (const file of accepted) {
          const piece = await uploadFileToCommunication(id, file);
          uploaded.push(piece);
        }
        setPiecesJointes((prev) => [...prev, ...uploaded]);
      } catch (error) {
        console.error('Erreur upload pièce jointe:', error);
        alert(error.response?.data?.message || 'Erreur lors de l\'ajout de la pièce jointe');
      } finally {
        setUploadingFiles(false);
      }
    } else {
      setPendingFiles((prev) => [...prev, ...accepted]);
    }
  };

  const handleRemovePendingFile = (index) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleRemovePieceJointe = async (pieceJointeId) => {
    if (!id) return;
    if (!window.confirm('Supprimer cette pièce jointe ?')) return;
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/communications/${id}/pieces-jointes/${pieceJointeId}`,
        { headers: getAuthHeaders() }
      );
      setPiecesJointes((prev) => prev.filter((pj) => pj._id !== pieceJointeId));
    } catch (error) {
      console.error('Erreur suppression pièce jointe:', error);
      alert(error.response?.data?.message || 'Erreur lors de la suppression');
    }
  };

  const handleDownloadPieceJointe = async (pieceJointeId, fallbackUrl) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/communications/${id}/pieces-jointes/${pieceJointeId}`,
        { headers: getAuthHeaders() }
      );
      if (response.data?.url) {
        window.open(response.data.url, '_blank');
      }
    } catch (error) {
      if (fallbackUrl) {
        window.open(fallbackUrl, '_blank');
        return;
      }
      console.error('Erreur téléchargement pièce jointe:', error);
      alert('Impossible de télécharger ce fichier');
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${process.env.REACT_APP_API_URL}/communications/${id}`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await uploadPendingFiles(id);
      alert('Modifications enregistrées avec succès');
      navigate('/admin/communications');
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      alert(error.response?.data?.message || 'Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e, action = 'brouillon', skipConfirmation = false) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    
    if (!formData.titre || !formData.contenu) {
      alert('Le titre et le contenu sont requis');
      return;
    }

    // Si action = programmer, afficher la modale de programmation (sauf si déjà confirmé)
    if (action === 'programme' && !skipConfirmation) {
      // Vérifier qu'il y a des destinataires sélectionnés
      if (!formData.estSanitaire && formData.criteresDestinataires.length === 0 && formData.groupesDestinataires.length === 0) {
        alert('Veuillez sélectionner au moins un destinataire');
        return;
      }
      // S'assurer que la prévisualisation est à jour
      if (!previewDestinataires) {
        await fetchPreviewDestinataires();
      }
      // Pré-remplir la date si elle existe déjà
      if (formData.dateProgrammee) {
        setScheduleDate(formData.dateProgrammee);
      } else {
        setScheduleDate('');
      }
      setPendingAction('programme');
      setShowScheduleModal(true);
      return;
    }

    // Si action = envoyer, afficher la modale de confirmation (sauf si déjà confirmé)
    if (action === 'envoyer' && !skipConfirmation) {
      if (!formData.estSanitaire && formData.criteresDestinataires.length === 0 && formData.groupesDestinataires.length === 0) {
        alert('Veuillez sélectionner au moins un destinataire');
        return;
      }
      setPendingAction('envoyer');
      setShowConfirmModal(true);
      return;
    }

    // Validation de la date de programmation si action = programme
    if (action === 'programme') {
      const dateToUse = skipConfirmation ? scheduleDate : formData.dateProgrammee;
      if (!dateToUse) {
        alert('Veuillez sélectionner une date de programmation');
        return;
      }
      const selectedDate = new Date(dateToUse);
      const now = new Date();
      if (selectedDate <= now) {
        alert('La date de programmation doit être dans le futur');
        return;
      }
      // Mettre à jour formData avec la date validée
      if (skipConfirmation) {
        formData.dateProgrammee = dateToUse;
      }
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const dataToSend = {
        ...formData,
        statut: action === 'envoyer' ? 'brouillon' : action === 'programme' ? 'programme' : 'brouillon'
      };

      let response;
      if (id) {
        // Mise à jour
        response = await axios.put(
          `${process.env.REACT_APP_API_URL}/communications/${id}`,
          dataToSend,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
      } else {
        // Création
        response = await axios.post(
          `${process.env.REACT_APP_API_URL}/communications`,
          dataToSend,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
      }

      await uploadPendingFiles(response.data._id);

      // Si action "envoyer", envoyer maintenant (déjà confirmé via modale)
      if (action === 'envoyer') {

        await axios.post(
          `${process.env.REACT_APP_API_URL}/communications/${response.data._id}/send`,
          {},
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        alert('Communication envoyée avec succès !');
      }

      navigate('/admin/communications');
    } catch (error) {
      console.error('Erreur:', error);
      alert(error.response?.data?.message || 'Erreur lors de l\'enregistrement');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <button
            onClick={() => navigate('/admin/communications')}
            className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            Retour aux communications
          </button>
          <h1 className="text-2xl font-bold text-gray-900">
            {id ? 'Modifier la communication' : 'Nouvelle communication'}
          </h1>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Colonne principale */}
            <div className="lg:col-span-2 space-y-6">
              {/* Titre */}
              <div className="bg-white rounded-lg shadow p-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Titre / Objet de l'email *
                </label>
                <input
                  type="text"
                  value={formData.titre}
                  onChange={(e) => setFormData({ ...formData, titre: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Ex: Intérêt général - Varroa"
                  required
                />
              </div>

              {/* Contenu */}
              <div className="bg-white rounded-lg shadow p-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Contenu du message *
                </label>
                <Editor
                  apiKey="yz5jdxbkp4i9v4omefqdsmbc6pxaqmyor9p6sh5cfi2f3gz5"
                  value={formData.contenu}
                  onEditorChange={(content) => setFormData({ ...formData, contenu: content })}
                  init={{
                    height: 400,
                    menubar: false,
                    plugins: [
                      'lists', 'link', 'code'
                    ],
                    toolbar: 'undo redo | bold italic underline | alignleft aligncenter alignright | bullist numlist | link | code',
                    content_style: 'body { font-family: Arial, sans-serif; font-size: 14px; }',
                    language: 'fr_FR'
                  }}
                />
                <p className="mt-2 text-xs text-gray-500">
                  Le contenu sera inséré dans un template HTML avec header et footer automatiques.
                </p>
              </div>

              {/* Pièces jointes */}
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Pièces jointes
                  </label>
                  <span className="text-xs text-gray-500">
                    {piecesJointes.length + pendingFiles.length}/{MAX_ATTACHMENTS} · max 10 Mo / fichier
                  </span>
                </div>
                <p className="text-xs text-gray-500 mb-4">
                  PDF, Word, Excel, PowerPoint ou images. Taille totale limitée à 20 Mo (contrainte d'envoi email).
                </p>

                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept={ALLOWED_EXTENSIONS.join(',')}
                  onChange={handleFilesSelected}
                  className="hidden"
                />

                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={loading || uploadingFiles || piecesJointes.length + pendingFiles.length >= MAX_ATTACHMENTS}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ArrowUpTrayIcon className="h-4 w-4 mr-2" />
                  {uploadingFiles ? 'Envoi en cours…' : 'Ajouter un document'}
                </button>

                {(piecesJointes.length > 0 || pendingFiles.length > 0) && (
                  <ul className="mt-4 divide-y divide-gray-100 border border-gray-200 rounded-md">
                    {piecesJointes.map((pj) => (
                      <li key={pj._id} className="flex items-center justify-between px-3 py-2">
                        <button
                          type="button"
                          onClick={() => handleDownloadPieceJointe(pj._id, pj.url)}
                          className="flex items-center min-w-0 text-left text-sm text-indigo-700 hover:text-indigo-900"
                        >
                          <PaperClipIcon className="h-4 w-4 mr-2 flex-shrink-0" />
                          <span className="truncate">{pj.nomOriginal || pj.nom}</span>
                          <span className="ml-2 text-xs text-gray-400 flex-shrink-0">{formatFileSize(pj.taille)}</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRemovePieceJointe(pj._id)}
                          className="ml-3 text-red-500 hover:text-red-700"
                          title="Supprimer"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </li>
                    ))}
                    {pendingFiles.map((file, index) => (
                      <li key={`pending-${file.name}-${index}`} className="flex items-center justify-between px-3 py-2 bg-amber-50">
                        <span className="flex items-center min-w-0 text-sm text-gray-700">
                          <PaperClipIcon className="h-4 w-4 mr-2 flex-shrink-0 text-amber-600" />
                          <span className="truncate">{file.name}</span>
                          <span className="ml-2 text-xs text-gray-400 flex-shrink-0">{formatFileSize(file.size)}</span>
                          <span className="ml-2 text-xs text-amber-700 flex-shrink-0">(en attente d'enregistrement)</span>
                        </span>
                        <button
                          type="button"
                          onClick={() => handleRemovePendingFile(index)}
                          className="ml-3 text-red-500 hover:text-red-700"
                          title="Retirer"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            {/* Colonne latérale */}
            <div className="space-y-6">
              {/* Statut */}
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-sm font-medium text-gray-900 mb-3">Statut</h3>
                <div className="flex items-center">
                  {!id ? (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
                      <span className="w-2 h-2 mr-2 rounded-full bg-gray-400"></span>
                      Non enregistrée
                    </span>
                  ) : communicationStatus === 'brouillon' ? (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
                      <span className="w-2 h-2 mr-2 rounded-full bg-gray-500"></span>
                      Brouillon
                    </span>
                  ) : communicationStatus === 'programme' ? (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                      <span className="w-2 h-2 mr-2 rounded-full bg-blue-500"></span>
                      Programmée
                    </span>
                  ) : communicationStatus === 'envoye' ? (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                      <span className="w-2 h-2 mr-2 rounded-full bg-green-500"></span>
                      Envoyée
                    </span>
                  ) : null}
                </div>
                {communicationStatus === 'programme' && formData.dateProgrammee && (
                  <p className="mt-2 text-xs text-gray-500">
                    Envoi prévu le {new Date(formData.dateProgrammee).toLocaleDateString('fr-FR', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                )}
              </div>

              {/* Options */}
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-sm font-medium text-gray-900 mb-4">Options</h3>
                
                {/* Intérêt général */}
                <div className="mb-4">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.estSanitaire}
                      onChange={(e) => setFormData({ ...formData, estSanitaire: e.target.checked })}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      Intérêt général
                    </span>
                  </label>
                  <p className="mt-1 text-xs text-gray-500 ml-6">
                    Envoyé à tous les adhérents <b>actifs</b> qui acceptent les informations d'intérêt général
                  </p>
                </div>

                {/* Destinataires - Nouveau système avec checkboxes */}
                {!formData.estSanitaire && (
                  <div className="border-t border-gray-200 pt-4 mb-2">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Destinataires
                    </label>
                    
                    {loadingStats ? (
                      <p className="text-sm text-gray-500">Chargement...</p>
                    ) : (
                      <div>
                        <div className="flex border-b border-gray-200 mb-3" role="tablist" aria-label="Organismes">
                          {ALL_ORGANISMES.map((organisme) => {
                            const selectionCount =
                              formData.criteresDestinataires.filter((c) => c.organisme === organisme).length +
                              formData.groupesDestinataires.filter((g) => g.organisme === organisme).length;
                            const isActive = destinataireTab === organisme;
                            return (
                              <button
                                key={organisme}
                                type="button"
                                role="tab"
                                aria-selected={isActive}
                                onClick={() => setDestinataireTab(organisme)}
                                className={`flex-1 px-2 py-2 text-sm font-medium border-b-2 transition-colors ${
                                  isActive
                                    ? 'border-indigo-600 text-indigo-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                              >
                                {organisme}
                                {selectionCount > 0 && (
                                  <span className={`ml-1.5 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1 rounded-full text-xs font-semibold ${
                                    isActive ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-600'
                                  }`}>
                                    {selectionCount}
                                  </span>
                                )}
                              </button>
                            );
                          })}
                        </div>

                        {(() => {
                          const organisme = destinataireTab;
                          const annees = Array.from(new Set(
                            destinataireStatsMerged
                              .filter(s => s.organisme === organisme)
                              .map(s => s.annee)
                          ));
                          const conseilStat = groupeStats.find(g => g.organisme === organisme && g.groupe === 'conseil');
                          const bureauStat = groupeStats.find(g => g.organisme === organisme && g.groupe === 'bureau');
                          const conseilChecked = formData.groupesDestinataires.some(
                            g => g.organisme === organisme && g.groupe === 'conseil'
                          );
                          const bureauChecked = formData.groupesDestinataires.some(
                            g => g.organisme === organisme && g.groupe === 'bureau'
                          );
                          const toggleGroupe = (groupe, checked) => {
                            if (checked) {
                              setFormData({
                                ...formData,
                                groupesDestinataires: [...formData.groupesDestinataires, { organisme, groupe }]
                              });
                            } else {
                              setFormData({
                                ...formData,
                                groupesDestinataires: formData.groupesDestinataires.filter(
                                  g => !(g.organisme === organisme && g.groupe === groupe)
                                )
                              });
                            }
                          };

                          return (
                          <div>
                            <div className="mb-3 space-y-1 pb-2 border-b border-gray-100">
                              <label className="flex items-center cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={conseilChecked}
                                  onChange={(e) => toggleGroupe('conseil', e.target.checked)}
                                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                                />
                                <span className="ml-2 text-sm text-gray-700">
                                  {conseilStat?.label || (organisme === 'SAR' ? 'Membres du CS' : 'Membres du CA')}
                                  <span className="ml-1 text-gray-500">({conseilStat?.count ?? 0})</span>
                                </span>
                              </label>
                              <label className="flex items-center cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={bureauChecked}
                                  onChange={(e) => toggleGroupe('bureau', e.target.checked)}
                                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                                />
                                <span className="ml-2 text-sm text-gray-700">
                                  Membres du bureau
                                  <span className="ml-1 text-gray-500">({bureauStat?.count ?? 0})</span>
                                </span>
                              </label>
                            </div>
                            
                            <div className="space-y-2">
                              {annees.length === 0 && (
                                <p className="text-xs text-gray-500">Aucune adhésion listée pour cet organisme.</p>
                              )}
                              {annees.map(annee => (
                                <div key={`${organisme}-${annee}`}>
                                  <p className="text-sm font-medium text-gray-700 mb-1">Année {annee}</p>
                                  
                                  <div className="ml-3 space-y-1">
                                    {destinataireStatsMerged
                                      .filter(s => s.organisme === organisme && Number(s.annee) === Number(annee))
                                      .map(stat => {
                                        const isChecked = formData.criteresDestinataires.some(
                                          c => c.organisme === stat.organisme &&
                                               Number(c.annee) === Number(stat.annee) &&
                                               c.statut === stat.statut
                                        );
                                        
                                        return (
                                          <label key={`${stat.organisme}-${stat.annee}-${stat.statut}`} className="flex items-center cursor-pointer">
                                            <input
                                              type="checkbox"
                                              checked={isChecked}
                                              onChange={(e) => {
                                                const critere = { organisme: stat.organisme, annee: Number(stat.annee), statut: stat.statut };
                                                if (e.target.checked) {
                                                  setFormData({
                                                    ...formData,
                                                    criteresDestinataires: [...formData.criteresDestinataires, critere]
                                                  });
                                                } else {
                                                  setFormData({
                                                    ...formData,
                                                    criteresDestinataires: formData.criteresDestinataires.filter(
                                                      c => !(c.organisme === critere.organisme && Number(c.annee) === Number(critere.annee) && c.statut === critere.statut)
                                                    )
                                                  });
                                                }
                                              }}
                                              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                                            />
                                            <span className="ml-2 text-sm text-gray-700">
                                              {stat.statut === 'actif' ? 'Actifs' : 'Expirés'} 
                                              <span className="ml-1 text-gray-500">({stat.count})</span>
                                            </span>
                                          </label>
                                        );
                                      })}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                          );
                        })()}
                      </div>
                    )}
                    
                    {(formData.criteresDestinataires.length > 0 || formData.groupesDestinataires.length > 0) && (
                      <p className="mt-2 text-xs text-gray-500">
                        {formData.criteresDestinataires.length + formData.groupesDestinataires.length} critère(s) sélectionné(s)
                      </p>
                    )}
                    
                    {/* Prévisualisation du nombre de destinataires */}
                    {(formData.estSanitaire || formData.criteresDestinataires.length > 0 || formData.groupesDestinataires.length > 0) && (
                      <div className="mt-3 p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
                        {loadingPreview ? (
                          <p className="text-sm text-indigo-700">Calcul en cours...</p>
                        ) : previewDestinataires ? (
                          <p className="text-sm font-medium text-indigo-900">
                            📧 {previewDestinataires.nombreTotal} destinataire{previewDestinataires.nombreTotal > 1 ? 's' : ''} unique{previewDestinataires.nombreTotal > 1 ? 's' : ''}
                          </p>
                        ) : (
                          <p className="text-sm text-indigo-700">Aucun destinataire trouvé</p>
                        )}
                      </div>
                    )}
                  </div>
                )}
                
                {/* Envoyer des copies aux organismes */}
                <div className="mb-4 border-t border-gray-200 pt-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">Envoyer une copie à</p>
                  <p className="text-xs text-gray-500 mb-2">
                    Une copie sera envoyée à l'adresse email de chaque organisme coché.
                  </p>
                  <div className="space-y-1 ml-1">
                    {['SAR', 'AMAIR', 'ADAR'].map((org) => (
                      <label key={org} className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.copieOrganismes.includes(org)}
                          onChange={(e) => {
                            const updated = e.target.checked
                              ? [...formData.copieOrganismes, org]
                              : formData.copieOrganismes.filter(o => o !== org);
                            setFormData({ ...formData, copieOrganismes: updated });
                          }}
                          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-sm text-gray-700">{org}</span>
                      </label>
                    ))}
                  </div>
                </div>

              </div>

              {/* Actions */}
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-sm font-medium text-gray-900 mb-4">Actions</h3>
                <div className="space-y-2">
                  {/* Bouton Sauvegarder pour les communications existantes */}
                  {id && (communicationStatus === 'brouillon' || communicationStatus === 'programme') && (
                    <>
                      <button
                        type="button"
                        onClick={handleSave}
                        disabled={loading || uploadingFiles}
                        className="w-full px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 disabled:opacity-50"
                      >
                        Sauvegarder
                      </button>
                      <hr className="my-3 border-gray-300" />
                    </>
                  )}
                  
                  <button
                    type="submit"
                    disabled={loading || uploadingFiles}
                    className="w-full px-4 py-2 bg-gray-600 text-white text-sm font-medium rounded-md hover:bg-gray-700 disabled:opacity-50"
                  >
                    {communicationStatus === 'programme' ? 'Repasser en brouillon' : 'Enregistrer comme brouillon'}
                  </button>
                  <button
                    type="button"
                    onClick={(e) => handleSubmit(e, 'programme')}
                    disabled={loading || uploadingFiles}
                    className="w-full px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    {communicationStatus === 'programme' ? 'Changer la date d\'envoi' : 'Choisir la date d\'envoi'}
                  </button>
                  <button
                    type="button"
                    onClick={(e) => handleSubmit(e, 'envoyer')}
                    disabled={loading || uploadingFiles}
                    className="w-full px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 disabled:opacity-50"
                  >
                    Envoyer maintenant
                  </button>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>

      {/* Modale de programmation */}
      {showScheduleModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4">
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Programmer la communication
              </h3>
              
              {previewDestinataires ? (
                <div className="space-y-4">
                  <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                    <p className="text-sm font-medium text-indigo-900 mb-2">
                      📧 Cette communication sera envoyée à :
                    </p>
                    <p className="text-2xl font-bold text-indigo-900">
                      {previewDestinataires.nombreTotal} destinataire{previewDestinataires.nombreTotal > 1 ? 's' : ''} unique{previewDestinataires.nombreTotal > 1 ? 's' : ''}
                    </p>
                    {(piecesJointes.length + pendingFiles.length) > 0 && (
                      <p className="mt-2 text-sm text-indigo-800">
                        📎 {piecesJointes.length + pendingFiles.length} pièce{piecesJointes.length + pendingFiles.length > 1 ? 's' : ''} jointe{piecesJointes.length + pendingFiles.length > 1 ? 's' : ''}
                      </p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Date et heure d'envoi programmé
                    </label>
                    <input
                      type="datetime-local"
                      value={scheduleDate}
                      onChange={(e) => setScheduleDate(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                    <p className="mt-2 text-xs text-gray-500">
                      La communication sera envoyée automatiquement à la date et l'heure sélectionnées
                    </p>
                  </div>
                  
                  <p className="text-sm text-gray-600">
                    Les doublons ont été automatiquement éliminés. Chaque utilisateur ne recevra qu'un seul email, même s'il correspond à plusieurs critères.
                  </p>
                </div>
              ) : (
                <p className="text-sm text-gray-600">Chargement des informations...</p>
              )}
              
              <div className="mt-6 flex gap-3">
                <button
                  onClick={() => {
                    setShowScheduleModal(false);
                    setPendingAction(null);
                    setScheduleDate('');
                  }}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
                >
                  Annuler
                </button>
                <button
                  onClick={() => {
                    setShowScheduleModal(false);
                    handleSubmit(null, pendingAction, true);
                  }}
                  disabled={!previewDestinataires || previewDestinataires.nombreTotal === 0 || !scheduleDate}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Confirmer la programmation
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modale de confirmation avant envoi */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4">
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Confirmer l'envoi de la communication
              </h3>
              
              {previewDestinataires ? (
                <div className="space-y-4">
                  <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                    <p className="text-sm font-medium text-indigo-900 mb-2">
                      📧 Cette communication sera envoyée à :
                    </p>
                    <p className="text-2xl font-bold text-indigo-900">
                      {previewDestinataires.nombreTotal} destinataire{previewDestinataires.nombreTotal > 1 ? 's' : ''} unique{previewDestinataires.nombreTotal > 1 ? 's' : ''}
                    </p>
                    {(piecesJointes.length + pendingFiles.length) > 0 && (
                      <p className="mt-2 text-sm text-indigo-800">
                        📎 {piecesJointes.length + pendingFiles.length} pièce{piecesJointes.length + pendingFiles.length > 1 ? 's' : ''} jointe{piecesJointes.length + pendingFiles.length > 1 ? 's' : ''}
                      </p>
                    )}
                  </div>
                  
                  <p className="text-sm text-gray-600">
                    Les doublons ont été automatiquement éliminés. Chaque utilisateur ne recevra qu'un seul email, même s'il correspond à plusieurs critères.
                  </p>
                  
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                    <p className="text-sm text-yellow-800">
                      ⚠️ Cette action est irréversible. Êtes-vous sûr de vouloir envoyer cette communication maintenant ?
                    </p>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-gray-600">Chargement des informations...</p>
              )}
              
              <div className="mt-6 flex gap-3">
                <button
                  onClick={() => {
                    setShowConfirmModal(false);
                    setPendingAction(null);
                  }}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
                >
                  Annuler
                </button>
                <button
                  onClick={() => {
                    setShowConfirmModal(false);
                    handleSubmit(null, pendingAction, true);
                  }}
                  disabled={!previewDestinataires || previewDestinataires.nombreTotal === 0}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Confirmer l'envoi
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
